import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import {promptChunkReload} from "../common/chunk-load-error";

/**
 * Tells the app to show its global loader while something the user just clicked is still being
 * fetched.
 *
 * Why this exists: feature code is split into lazy chunks, so a click can mean "download a chunk
 * first". Without feedback the screen simply does nothing for a few seconds and the app looks
 * broken. Router navigations are covered by AppComponent listening to router events; this service
 * covers the other case - a dynamic import() fired from a service or a component, such as opening
 * the upgrade wizard or the rich text editor.
 *
 * Once a chunk has been downloaded the browser caches it and the module is already evaluated, so
 * the second call resolves immediately. AppComponent only paints the loader if the work is still
 * running after a short delay, so a cached or preloaded module shows nothing at all.
 *
 * Usage:
 *     this.busy.run(() => import("./some-heavy-dialog.component")).then(({X}) => this.dialog.open(X));
 */
@Injectable({providedIn: "root"})
export class AppBusyService {

    private count = 0;
    private readonly busySubject = new BehaviorSubject<boolean>(false);

    /** true whenever at least one tracked piece of work is in flight */
    readonly busy$: Observable<boolean> = this.busySubject.asObservable();

    /**
     * Runs `work` with the app marked busy, and clears it again whether the work succeeds or
     * fails. Nested / concurrent calls are counted, so the loader only clears once the last one
     * finishes.
     */
    run<T>(work: () => Promise<T>): Promise<T> {
        this.begin();
        let promise: Promise<T>;
        try {
            promise = work();
        } catch (e) {
            // a synchronous throw must not leave the loader stuck on screen
            this.end();
            promptChunkReload(e);
            return Promise.reject(e);
        }
        return promise
            .catch((e) => {
                // The chunk is gone because the app was redeployed under this tab. Callers only
                // console.error their own failure, so without this the click would simply do
                // nothing forever. Handled here so every caller of run() is covered.
                promptChunkReload(e);
                throw e;
            })
            .finally(() => this.end());
    }

    begin(): void {
        this.count++;
        if (this.count === 1) {
            this.busySubject.next(true);
        }
    }

    end(): void {
        this.count = Math.max(0, this.count - 1);
        if (this.count === 0 && this.busySubject.value) {
            this.busySubject.next(false);
        }
    }
}
